"use client";

import { useMemo } from "react";
import { formatEther } from "viem";
import { useBalance } from "wagmi";
import { PRIMARY_CHAIN_ID } from "@/constants/chain";
import { TOKENS } from "@/constants/tokens";
import { useLogin } from "@/hooks/auth";
import { useTokenBalances } from "@/hooks/data/use-token-balances";
import { usePrice } from "@/hooks/haiku";
import { useReadBgtBalanceOf } from "@/lib/onchain/generated";

export type Holding = {
  key: string;
  type: "token" | "gauge" | "native";
  label: string;
  unit: string;
  icon: string;
  address?: `0x${string}` | null;
  amountRaw: bigint;
  amount: number;
  usd: number; // numeric USD value for sorting/sum
};

export function usePortfolioBalances(opts?: {
  address?: `0x${string}`;
  chainId?: number;
  includeGauges?: boolean;
}) {
  const { wallets } = useLogin();
  const address = (opts?.address || (wallets.EVM?.address as `0x${string}`)) as
    | `0x${string}`
    | undefined;
  const chainId = opts?.chainId || PRIMARY_CHAIN_ID;
  const includeGauges = opts?.includeGauges !== false; // default true

  // Prices
  const priceHenlo = usePrice({ symbol: "HENLO" }).data?.usd || 0;
  const priceBgt = usePrice({ symbol: "BGT" }).data?.usd || 0;
  const priceHoney = usePrice({ symbol: "HONEY" }).data?.usd || 0;
  const priceBera = usePrice({ symbol: "BERA" }).data?.usd || 0;
  const priceObero = usePrice({ symbol: "OBERO" }).data?.usd || 0;

  // Native BERA
  const { data: beraBal, isLoading: beraLoading } = useBalance({
    address,
    chainId,
    query: { enabled: !!address },
  });

  // ERC‑20 balances via batch read (all tokens with addresses, including gauges & BGT)
  const tokenAddresses = TOKENS.filter((t) => t.type !== "native" && t.address).map(
    (t) => t.address as `0x${string}`,
  );
  const { balances: erc20Balances, isLoading: erc20Loading } = useTokenBalances({
    chainId,
    address,
    tokens: tokenAddresses,
  });

  // Direct BGT read via generated hook
  const { data: bgtBal, isLoading: bgtLoading } = useReadBgtBalanceOf({
    args: address ? [address] : (undefined as any),
    query: { enabled: !!address },
  });

  // Gauges use the same erc20Balances map (addresses included above)

  const holdings = useMemo(() => {
    const list: Holding[] = [];
    const add = (h: Omit<Holding, "amount" | "usd"> & { price?: number }) => {
      const amount = Number(formatEther(h.amountRaw));
      const usd = (h.price || 0) * amount;
      list.push({ ...h, amount, usd });
    };

    // All tokens from registry (optionally filter gauges)
    const visibleTokens = TOKENS.filter((t) => includeGauges || t.type !== "gauge");
    for (const t of visibleTokens) {
      if (t.type === "native") {
        add({
          key: t.key,
          type: t.type,
          label: t.symbol,
          unit: t.unit || t.symbol,
          icon: t.icon,
          address: null,
          amountRaw: beraBal?.value || 0n,
          price: priceBera,
        });
        continue;
      }

      const priceBySymbol: Record<string, number> = {
        HENLO: priceHenlo,
        BGT: priceBgt,
        HONEY: priceHoney,
        OBERO: priceObero,
      } as any;

      const batchVal = erc20Balances.get(t.address as `0x${string}`) || 0n;
      const amountRaw = t.symbol === "BGT" ? (bgtBal ?? batchVal) : batchVal;

      add({
        key: t.key,
        type: t.type,
        label: t.symbol,
        unit: t.unit || t.symbol,
        icon: t.icon,
        address: t.address as `0x${string}`,
        amountRaw,
        price: priceBySymbol[t.symbol] || 0,
      });
    }

    return list;
  }, [
    beraBal?.value,
    erc20Balances,
    bgtBal,
    priceBera,
    priceHenlo,
    priceHoney,
    priceObero,
    priceBgt,
    includeGauges,
  ]);

  const sortedHoldings = useMemo(() => {
    return [...holdings].sort((a, b) => {
      const aHeld = a.amount > 0;
      const bHeld = b.amount > 0;
      if (aHeld !== bHeld) return aHeld ? -1 : 1; // held first
      if (aHeld && bHeld) {
        if (b.usd !== a.usd) return b.usd - a.usd; // high USD first
        if (b.amount !== a.amount) return b.amount - a.amount; // then amount
        return a.label.localeCompare(b.label);
      }
      // neither held
      return a.label.localeCompare(b.label);
    });
  }, [holdings]);

  const totalUSD = useMemo(() => holdings.reduce((sum, h) => sum + (h.usd || 0), 0), [holdings]);

  const henloAmount = useMemo(
    () => holdings.find((h) => h.key === "HENLO")?.amount || 0,
    [holdings],
  );

  const beraAmount = useMemo(
    () => holdings.find((h) => h.key === "BERA")?.amount || 0,
    [holdings],
  );

  const isLoading = beraLoading || erc20Loading || bgtLoading;

  return {
    address,
    holdings,
    sortedHoldings,
    totalUSD,
    henloAmount,
    beraAmount,
    isLoading,
  } as const;
}
